import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { validateInvite } from '../../api/invites';
import { initGoogleAuth } from '../../api/auth';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Loading } from '../../components/common/Loading';
import { ROUTES } from '../../utils/constants';

interface JoinProps {
  tenant: string;
}

export function Join({ tenant }: JoinProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuthStore();
  const { config } = useTenantStore();

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inviteToken = searchParams.get('invite');

  useEffect(() => {
    if (!inviteToken) {
      setError('No invite token provided');
      setIsValidating(false);
      return;
    }

    validateInviteToken();
  }, [inviteToken]);

  useEffect(() => {
    // If user is already logged in, redirect to home
    if (user && !authLoading) {
      navigate(ROUTES.home(tenant));
    }
  }, [user, authLoading, tenant, navigate]);

  const validateInviteToken = async () => {
    try {
      const response = await validateInvite(tenant, inviteToken!);
      if (response.valid) {
        setIsValid(true);
        // Store the invite token in session storage so we can use it after OAuth
        sessionStorage.setItem('invite_token', inviteToken!);
      } else {
        setError(response.error || 'Invalid invite link');
      }
    } catch (err) {
      setError('Failed to validate invite link');
    } finally {
      setIsValidating(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const response = await initGoogleAuth(tenant);
      window.location.href = response.authUrl;
    } catch (err) {
      setError('Failed to start sign in');
    }
  };

  if (authLoading || isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loading message="Validating invite..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <div className="text-center mb-6">
          {config?.branding.logoUrl ? (
            <img
              src={config.branding.logoUrl}
              alt={config.name}
              className="h-12 mx-auto mb-4"
            />
          ) : (
            <h1 className="text-2xl font-bold text-primary-600">
              {config?.name || 'OnlyCoach'}
            </h1>
          )}
        </div>

        {error ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Invalid Invite</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button variant="outline" onClick={() => navigate(ROUTES.login(tenant))}>
              Go to Login
            </Button>
          </div>
        ) : isValid ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">You're Invited!</h2>
            <p className="text-gray-600 mb-6">
              You've been invited to join {config?.name || 'OnlyCoach'}. Sign in with Google to create your account.
            </p>

            <Button
              variant="primary"
              className="w-full"
              onClick={handleGoogleSignIn}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
